import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import mtStyles from "./ManageTemplatesModal.module.css";

interface TemplateChannel {
  id?: number;
  channel: string;
  display_position: string;
  min_value: number | null;
  max_value: number | null;
  axis: number | null;
}

interface Template {
  id: number;
  template_name: string;
  widget_type: string;
  is_global?: boolean;
  can_edit?: boolean;
  channels: TemplateChannel[];
}

interface PumpChannel {
  value: string;
  label: string;
}

interface ManageTemplatesModalProps {
  show: boolean;
  onHide: () => void;
  onBackToSettings?: () => void;
  theme: string;
  templates: Template[];
  onTemplatesChange: () => void;
  selectedChannels: PumpChannel[];
  selectedTableChannels: PumpChannel[];
  axisSettings: {
    [axis: string]: {
      channels: string[];
      min: number | undefined;
      max: number | undefined;
    };
  };
  availableChannels: PumpChannel[];
}

const ManageTemplatesModal: React.FC<ManageTemplatesModalProps> = ({
  show,
  onHide,
  onBackToSettings,
  theme,
  templates,
  onTemplatesChange,
  selectedChannels,
  selectedTableChannels,
  axisSettings,
  availableChannels,
}) => {
  const [templateName, setTemplateName] = useState("");
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [editName, setEditName] = useState("");
  const [activeTab, setActiveTab] = useState<"CHART" | "TABLE">("CHART");
  const [isGlobalTemplate, setIsGlobalTemplate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<number | null>(null);

  const templateInputRef = useRef<HTMLInputElement | null>(null);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";
  const isDarkTheme = theme === "dark";

  const getUserRoles = () => {
    const token = localStorage.getItem("token");
    if (!token) return [];

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.roles || [];
    } catch {
      return [];
    }
  };

  const userRoles = getUserRoles();
  const canManageGlobalTemplates =
    userRoles.includes("Manager") || userRoles.includes("Editor");

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        templateInputRef.current?.focus();
      }, 0);
    }
  }, [show, activeTab]);

  useEffect(() => {
    if (!show) return;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
          setTemplateToDelete(null);
          return;
        }
        onHide();
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, [show, onHide, showDeleteConfirm]);

  if (!show) return null;

  const filteredTemplates = templates.filter(
    (t) => t.widget_type === activeTab,
  );

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.warning("Please enter a template name");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      let templateChannels;

      if (activeTab === "CHART") {
        templateChannels = selectedChannels.map((ch) => {
          const axisPosition =
            Object.keys(axisSettings).find((axis) =>
              axisSettings[axis].channels.includes(ch.value),
            ) || "left1";

          const axisNumber = parseInt(axisPosition.match(/\d+/)?.[0] || "1");

          return {
            channel: ch.value,
            display_position: axisPosition,
            min_value: axisSettings[axisPosition].min ?? null,
            max_value: axisSettings[axisPosition].max ?? null,
            axis: axisNumber,
          };
        });
      } else {
        templateChannels = selectedTableChannels.map((ch, index) => ({
          channel: ch.value,
          display_position: `position_${index + 1}`,
          min_value: null,
          max_value: null,
          axis: null,
        }));
      }

      await axios.post(
        `${backendUrl}/widget-templates`,
        {
          template_name: templateName,
          widget_type: activeTab,
          channels: templateChannels,
          is_global: canManageGlobalTemplates && isGlobalTemplate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast.success("Template saved successfully");
      setTemplateName("");
      setIsGlobalTemplate(false);
      onTemplatesChange();
    } catch (error: any) {
      console.error("Error saving template:", error);
      if (error.response?.status === 409) {
        toast.error("Template name already exists");
      } else {
        toast.error("Failed to save template");
      }
    }
  };

  const handleEditTemplate = async (template: Template) => {
    if (!editName.trim()) {
      toast.warning("Please enter a template name");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      let templateChannels;

      if (template.widget_type === "CHART") {
        templateChannels = selectedChannels.map((ch) => {
          const axisPosition =
            Object.keys(axisSettings).find((axis) =>
              axisSettings[axis].channels.includes(ch.value),
            ) || "left1";

          const axisNumber = parseInt(axisPosition.match(/\d+/)?.[0] || "1");

          return {
            channel: ch.value,
            display_position: axisPosition,
            min_value: axisSettings[axisPosition].min ?? null,
            max_value: axisSettings[axisPosition].max ?? null,
            axis: axisNumber,
          };
        });
      } else {
        templateChannels = selectedTableChannels.map((ch, index) => ({
          channel: ch.value,
          display_position: `position_${index + 1}`,
          min_value: null,
          max_value: null,
          axis: null,
        }));
      }

      await axios.put(
        `${backendUrl}/templates/${template.id}`,
        {
          template_name: editName,
          widget_type: template.widget_type,
          channels: templateChannels,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success("Template updated successfully");
      setEditingTemplate(null);
      setEditName("");
      onTemplatesChange();
    } catch (error: any) {
      console.error("Error updating template:", error);
      if (error.response?.status === 409) {
        toast.error("Template name already exists");
      } else {
        toast.error("Failed to update template");
      }
    }
  };

  const handleDeleteTemplate = (templateId: number) => {
    setTemplateToDelete(templateId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      await axios.delete(`${backendUrl}/widget-templates/${templateToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Template deleted successfully");
      onTemplatesChange();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    } finally {
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
    }
  };

  const startEdit = (template: Template) => {
    setEditingTemplate(template);
    setEditName(template.template_name);
  };

  const cancelEdit = () => {
    setEditingTemplate(null);
    setEditName("");
  };

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1040,
        }}
        onClick={onHide}
      />

      <div
        style={{
          position: "fixed",
          top: "max(72px, 50%)",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 3001,
          width: "90%",
          maxWidth: "650px",
          backgroundColor: isDarkTheme ? "#303335" : "#ffffff",
          borderRadius: "8px",
          boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
          maxHeight: "calc(100vh - 88px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "1rem 1.5rem",
            borderBottom: `1px solid ${isDarkTheme ? "#5c646b" : "#e6e6e6"}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            <h5
              style={{
                margin: 0,
                color: isDarkTheme ? "#e5e5e5" : "#333333",
                fontSize: "1.25rem",
                fontWeight: 600,
              }}
            >
              Manage Templates
            </h5>
          </div>
          <button
            onClick={onHide}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: isDarkTheme ? "#e5e5e5" : "#333333",
              padding: "0",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${isDarkTheme ? "#5c646b" : "#e6e6e6"}`,
            padding: "0 1.5rem",
          }}
        >
          <button
            onClick={() => setActiveTab("CHART")}
            style={{
              padding: "0.75rem 1.5rem",
              border: "none",
              borderBottom:
                activeTab === "CHART"
                  ? "2px solid #8CC540"
                  : "2px solid transparent",
              backgroundColor: "transparent",
              color:
                activeTab === "CHART"
                  ? isDarkTheme
                    ? "#e5e5e5"
                    : "#333333"
                  : isDarkTheme
                    ? "#8b9299"
                    : "#999999",
              cursor: "pointer",
              fontWeight: activeTab === "CHART" ? 600 : 500,
              fontSize: "0.875rem",
              transition: "all 0.2s",
            }}
          >
            Charts
          </button>
          <button
            onClick={() => setActiveTab("TABLE")}
            style={{
              padding: "0.75rem 1.5rem",
              border: "none",
              borderBottom:
                activeTab === "TABLE"
                  ? "2px solid #8CC540"
                  : "2px solid transparent",
              backgroundColor: "transparent",
              color:
                activeTab === "TABLE"
                  ? isDarkTheme
                    ? "#e5e5e5"
                    : "#333333"
                  : isDarkTheme
                    ? "#8b9299"
                    : "#999999",
              cursor: "pointer",
              fontWeight: activeTab === "TABLE" ? 600 : 500,
              fontSize: "0.875rem",
              transition: "all 0.2s",
            }}
          >
            Tables
          </button>
        </div>

        <div
          style={{
            padding: "1.5rem",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          <div className={mtStyles.templateNameGroup}>
            <label className={mtStyles.templateNameLabel}>Template Name</label>
            <div className={mtStyles.templateNameRow}>
              <input
                ref={templateInputRef}
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name"
                className={mtStyles.templateNameInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveTemplate();
                  }
                }}
              />
              <button
                onClick={handleSaveTemplate}
                className={mtStyles.templateSaveBtn}
              >
                Save Template
              </button>
            </div>
          </div>

          {canManageGlobalTemplates && (
            <div className={mtStyles.globalTemplateRow}>
              <label className={mtStyles.globalTemplateLabel}>
                <input
                  type="checkbox"
                  checked={isGlobalTemplate}
                  onChange={(e) => setIsGlobalTemplate(e.target.checked)}
                />
                <span>Make this a global template</span>
              </label>
            </div>
          )}

          <div>
            <h6
              style={{
                margin: "0 0 1rem 0",
                color: isDarkTheme ? "#e5e5e5" : "#333333",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Available Templates
            </h6>

            {filteredTemplates.length === 0 ? (
              <p
                style={{
                  color: isDarkTheme ? "#8b9299" : "#999999",
                  fontSize: "0.875rem",
                  textAlign: "center",
                  padding: "2rem 1rem",
                }}
              >
                No templates available
              </p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    style={{
                      padding: "0.75rem 1rem",
                      borderRadius: "4px",
                      backgroundColor: isDarkTheme ? "#3d4247" : "#f8f9fa",
                      border: `1px solid ${isDarkTheme ? "#5c646b" : "#e6e6e6"}`,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {editingTemplate?.id === template.id ? (
                      <>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{
                            flex: 1,
                            padding: "0.375rem 0.5rem",
                            borderRadius: "4px",
                            border: `1px solid ${isDarkTheme ? "#5c646b" : "#d0d5dd"}`,
                            backgroundColor: isDarkTheme
                              ? "#303335"
                              : "#ffffff",
                            color: isDarkTheme ? "#e5e5e5" : "#333333",
                            fontSize: "0.875rem",
                            marginRight: "0.5rem",
                            outline: "none",
                          }}
                          autoFocus
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleEditTemplate(template);
                            } else if (e.key === "Escape") {
                              cancelEdit();
                            }
                          }}
                        />
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button
                            onClick={() => handleEditTemplate(template)}
                            style={{
                              padding: "0.375rem 0.75rem",
                              border: `1px solid #22c55e`,
                              borderRadius: "4px",
                              backgroundColor: "#22c55e",
                              color: "#ffffff",
                              cursor: "pointer",
                              fontSize: "0.8125rem",
                              fontWeight: 500,
                              transition: "background-color 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#16a34a";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#22c55e";
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            style={{
                              padding: "0.375rem 0.75rem",
                              border: `1px solid ${isDarkTheme ? "#5c646b" : "#6c757d"}`,
                              borderRadius: "4px",
                              backgroundColor: "transparent",
                              color: isDarkTheme ? "#e5e5e5" : "#6c757d",
                              cursor: "pointer",
                              fontSize: "0.8125rem",
                              fontWeight: 500,
                              transition: "background-color 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                isDarkTheme ? "#404040" : "#f8f9fa";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <span
                            style={{
                              color: isDarkTheme ? "#e5e5e5" : "#333333",
                              fontWeight: 500,
                              fontSize: "0.875rem",
                            }}
                          >
                            {template.template_name}
                          </span>

                          {template.is_global && (
                            <span
                              style={{
                                backgroundColor: "#2563eb",
                                color: "#ffffff",
                                padding: "2px 8px",
                                borderRadius: "999px",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                              }}
                            >
                              GLOBAL
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button
                            onClick={() => {
                              const event = new CustomEvent("applyTemplate", {
                                detail: template,
                              });
                              window.dispatchEvent(event);
                              onHide();
                            }}
                            style={{
                              padding: "0.375rem 0.75rem",
                              border: "1px solid #8CC540",
                              borderRadius: "4px",
                              backgroundColor: "#8CC540",
                              color: "#ffffff",
                              cursor: "pointer",
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#7AB438";
                              e.currentTarget.style.borderColor = "#7AB438";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#8CC540";
                              e.currentTarget.style.borderColor = "#8CC540";
                            }}
                          >
                            Apply
                          </button>
                          {template.can_edit === true ? (
                            <>
                              <button
                                onClick={() => startEdit(template)}
                                style={{
                                  padding: "0.375rem 0.75rem",
                                  border: `1px solid #eab308`,
                                  borderRadius: "4px",
                                  backgroundColor: "transparent",
                                  color: "#eab308",
                                  cursor: "pointer",
                                  fontSize: "0.8125rem",
                                  fontWeight: 500,
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#eab308";
                                  e.currentTarget.style.color = "#ffffff";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                  e.currentTarget.style.color = "#eab308";
                                }}
                              >
                                Edit
                              </button>

                              <button
                                onClick={() =>
                                  handleDeleteTemplate(template.id)
                                }
                                style={{
                                  padding: "0.375rem 0.75rem",
                                  border: "1px solid #dc3545",
                                  borderRadius: "4px",
                                  backgroundColor: "transparent",
                                  color: "#dc3545",
                                  cursor: "pointer",
                                  fontSize: "0.8125rem",
                                  fontWeight: 500,
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#dc3545";
                                  e.currentTarget.style.color = "#ffffff";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                  e.currentTarget.style.color = "#dc3545";
                                }}
                              >
                                Delete
                              </button>
                            </>
                          ) : null}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            padding: "1rem 1.5rem",
            borderTop: `1px solid ${isDarkTheme ? "#5c646b" : "#e6e6e6"}`,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onHide}
            style={{
              padding: "0.5rem 1.5rem",
              border: "none",
              borderRadius: "4px",
              backgroundColor: isDarkTheme ? "#525252" : "#6c757d",
              color: "#ffffff",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDarkTheme
                ? "#404040"
                : "#5a6268";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDarkTheme
                ? "#525252"
                : "#6c757d";
            }}
          >
            Close
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 4000,
          }}
          onClick={() => {
            setShowDeleteConfirm(false);
            setTemplateToDelete(null);
          }}
        >
          <div
            style={{
              background: isDarkTheme ? "#2f343b" : "#ffffff",
              padding: "1.5rem",
              borderRadius: "10px",
              width: "380px",
              color: isDarkTheme ? "#e5e5e5" : "#333333",
              boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
              border: `1px solid ${isDarkTheme ? "#474d55" : "#e5e7eb"}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontWeight: 700,
                fontSize: "1rem",
                marginBottom: "0.875rem",
                color: isDarkTheme ? "#f3f4f6" : "#111827",
              }}
            >
              <span style={{ color: "#f59e0b", fontSize: "1.1rem" }}>⚠️</span>
              <span>Delete Template?</span>
            </div>

            <div
              style={{
                fontSize: "0.9rem",
                lineHeight: 1.5,
                marginBottom: "1.25rem",
                color: isDarkTheme ? "#d1d5db" : "#4b5563",
              }}
            >
              Are you sure you want to delete this template?
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.75rem",
              }}
            >
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setTemplateToDelete(null);
                }}
                style={{
                  padding: "0.45rem 0.95rem",
                  border: `1px solid ${isDarkTheme ? "#6b7280" : "#9ca3af"}`,
                  background: "transparent",
                  color: isDarkTheme ? "#e5e5e5" : "#4b5563",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkTheme
                    ? "#3f454d"
                    : "#f3f4f6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                style={{
                  padding: "0.45rem 0.95rem",
                  background: "#dc3545",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#b02a37";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc3545";
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageTemplatesModal;
